import React, { useState, useEffect } from 'react';
import { isAdmin } from '../config/accessControl';

function Instructions({ selectedCompany, apiBaseUrl, apiKey, userEmail }) {
  const [instruction, setInstruction] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [error, setError] = useState(null);
  const [editedInstruction, setEditedInstruction] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (selectedCompany) {
      fetchInstruction();
      setError(null);
    } else {
      setInstruction('');
      setEditedInstruction('');
    }
  }, [selectedCompany]);

  const fetchInstruction = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      console.log(`Fetching instruction for ${selectedCompany} from ${apiBaseUrl}/instruction/${selectedCompany}`);
      
      const response = await fetch(`${apiBaseUrl}/instruction/${selectedCompany}`, {
        headers: {
          'X-Access-Token': process.env.REACT_APP_CONNECTOR_API_KEY
        }
      });
      
      if (!response.ok) {
        throw new Error(`Server returned ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log("Instruction data received:", data);
      
      setInstruction(data.instruction);
      setEditedInstruction(data.instruction);
    } catch (error) {
      console.error('Error fetching instruction:', error);
      setError(`Failed to fetch instructions: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      console.log(`Saving instruction for ${selectedCompany} to ${apiBaseUrl}/instruction/${selectedCompany}`);
      const response = await fetch(`${apiBaseUrl}/instruction/${selectedCompany}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Access-Token': process.env.REACT_APP_CONNECTOR_API_KEY
        },
        body: JSON.stringify({ instruction: editedInstruction })
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Could not retrieve error text from response.');
        throw new Error(`Failed to save to main backend. Server returned ${response.status}: ${response.statusText}. Response: ${errorText}`);
      }
      console.log("Instruction saved to main backend successfully.");

      const aiEndpoint = process.env.REACT_APP_AI_ENDPOINT_URL;
      console.log("AI Endpoint URL from env:", aiEndpoint);

      if (!aiEndpoint) {
        console.warn("REACT_APP_AI_ENDPOINT_URL is not set. Skipping AI endpoint notifications.");
        // If AI endpoint notification is critical, you might want to throw an error here instead:
        // throw new Error("AI Endpoint URL (REACT_APP_AI_ENDPOINT_URL) is not configured. Cannot complete save operation.");
      } else {
        const companyUpdateUrl = `${aiEndpoint}/update-company`;
        const instructionUpdateUrl = `${aiEndpoint}/update-company-instruction`;
        
        console.log(`Notifying AI endpoint: POST ${companyUpdateUrl} for ${selectedCompany}`);
        const updateCompanyPromise = fetch(companyUpdateUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ company_name: selectedCompany })
        });

        console.log(`Notifying AI endpoint: POST ${instructionUpdateUrl} for ${selectedCompany}`);
        const updateInstructionPromise = fetch(instructionUpdateUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            company_name: selectedCompany,
            instruction: editedInstruction 
          })
        });

        const results = await Promise.all([
          updateCompanyPromise.then(async res => {
            if (!res.ok) {
              const errorText = await res.text().catch(() => 'Failed to get error text.');
              return Promise.reject(new Error(`AI Sync Error (/update-company): ${res.status} ${res.statusText}. Details: ${errorText}`));
            }
            return res.json().catch(() => res.text()); // Prefer JSON, fallback to text
          }),
          updateInstructionPromise.then(async res => {
            if (!res.ok) {
              const errorText = await res.text().catch(() => 'Failed to get error text.');
              return Promise.reject(new Error(`AI Sync Error (/update-company-instruction): ${res.status} ${res.statusText}. Details: ${errorText}`));
            }
            return res.json().catch(() => res.text()); // Prefer JSON, fallback to text
        })
      ]);
        
        console.log("AI endpoint notifications sent successfully. Responses:", results);
      }

      setIsEditing(false);
      setInstruction(editedInstruction);
    } catch (error) {
      console.error('Error saving instruction:', error);
      // The error message will now be more specific if it came from one of the fetch operations
      setError(`Failed to save instruction: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleVisibility = async () => {
    const adminStatus = await isAdmin(userEmail);
    if (!adminStatus) {
      alert('Instructions are only available to administrators.');
      return;
    }
    setIsVisible(!isVisible);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-3xl font-bold bg-gradient-to-r from-blue-500 to-teal-500 text-transparent bg-clip-text">
          Instructions for <span className="italic">{selectedCompany}</span>
        </h2>
        <button
          className="px-4 py-2 bg-blue-400 text-white rounded hover:bg-blue-500 transition duration-300"
          onClick={toggleVisibility}
        >
          {isVisible ? 'Hide' : 'Show'}
        </button>
      </div>
      
      {isLoading && <div className="text-gray-600">Loading...</div>}
      
      {error && (
        <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded mb-4">
          {error}
          <button 
            onClick={fetchInstruction} 
            className="ml-4 px-2 py-1 bg-red-200 rounded hover:bg-red-300"
          >
            Retry
          </button>
        </div>
      )}
      
      {!isLoading && !error && (
        isVisible && (
          isEditing ? (
            <div>
              <textarea
                className="w-full p-2 border rounded min-h-[50vh]"
                value={editedInstruction}
                onChange={(e) => setEditedInstruction(e.target.value)}
              />
              <div className="mt-4 space-x-2">
                <button
                  className="px-4 py-2 bg-blue-400 text-white rounded hover:bg-blue-500 transition duration-300"
                  onClick={handleSave}
                >
                  Save
                </button>
                <button
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition duration-300"
                  onClick={() => setIsEditing(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div>
              {instruction ? (
                <p className="whitespace-pre-wrap p-4 border rounded bg-gray-50">{instruction}</p>
              ) : (
                <p className="text-gray-500 italic">No instructions available for this company.</p>
              )}
              
              <div className="mt-4 flex space-x-2">
                <button
                  className="px-4 py-2 bg-blue-400 text-white rounded hover:bg-blue-500 transition duration-300"
                  onClick={() => setIsEditing(true)}
                >
                  Edit
                </button>
              </div>
            </div>
          )
        )
      )}
    </div>
  );
}

export default Instructions;
